import { useContext, useEffect, useState } from "react";
import { Alert, Button, Snackbar } from "@mui/material";
import { LanguageContext } from "../../config/ContextConfig";
import { BUILD_ID } from "../../generated/buildId";

/**
 * How often the running app asks the server whether a newer build was
 * deployed. The check also runs when the user returns to the tab, which
 * is the moment stale code actually starts to matter again.
 */
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

/**
 * The fingerprint served next to index.html. Written by
 * scripts/write-build-id.js on every build.
 */
const VERSION_URL = `${ process.env.PUBLIC_URL }/version.json`;

/**
 * Fetches the build id of the currently deployed frontend.
 *
 * A failed check (offline, proxy error, malformed response) is deliberately
 * swallowed: the version check must never break the application. Returning
 * null simply keeps the current state.
 */
async function fetchDeployedBuildId(): Promise< string | null > {
    try {
        // cache buster + no-store: neither the browser nor a proxy may ever
        // cache the fingerprint, otherwise updates would go unnoticed
        const response = await fetch( `${ VERSION_URL }?t=${ Date.now() }`, { cache: "no-store" } );
        if ( ! response.ok ) {
            return null;
        }
        const payload = await response.json();
        return typeof payload.buildId === "string" ? payload.buildId : null;
    } catch ( error ) {
        console.warn( "frontend version check failed", error );
        return null;
    }
}

/**
 * Detects a newly deployed frontend build and prompts the user to reload.
 *
 * Deep module: mount once at the app root, no props required. All internals
 * (polling interval, tab-focus checks, network failures, cache busting) are
 * hidden. Inactive in development mode.
 *
 * How it works: the build id compiled into this bundle (BUILD_ID) is compared
 * against the id served in version.json. When they differ, a snackbar with a
 * reload button is shown; reloading re-fetches index.html and with it the
 * bundle files of the new build.
 */
export function VersionCheckBanner() {
    const language = useContext( LanguageContext );
    const [ isNewVersionAvailable, setIsNewVersionAvailable ] = useState( false );

    useEffect( () => {
        if ( process.env.NODE_ENV !== "production" ) {
            return;
        }

        const checkForNewVersion = async () => {
            const deployedBuildId = await fetchDeployedBuildId();
            if ( deployedBuildId !== null && deployedBuildId !== BUILD_ID ) {
                setIsNewVersionAvailable( true );
            }
        };

        checkForNewVersion();
        const interval = setInterval( checkForNewVersion, CHECK_INTERVAL_MS );

        // the user might have the tab in the background for hours - check
        // right when she comes back instead of waiting for the next tick
        const onVisibilityChange = () => {
            if ( document.visibilityState === "visible" ) {
                checkForNewVersion();
            }
        };
        document.addEventListener( "visibilitychange", onVisibilityChange );

        return () => {
            clearInterval( interval );
            document.removeEventListener( "visibilitychange", onVisibilityChange );
        };
    }, [] );

    return (
        <Snackbar
            open={ isNewVersionAvailable }
            anchorOrigin={ { vertical: "bottom", horizontal: "center" } }
        >
            <Alert
                severity="info"
                action={
                    <Button
                        color="inherit"
                        size="small"
                        onClick={ () => window.location.reload() }
                    >
                        { language.versionCheck.reload }
                    </Button>
                }
            >
                { language.versionCheck.message }
            </Alert>
        </Snackbar>
    );
}
